<template>
    <div v-if="navigationData.length > 0" class="mega-nav" role="navigation" aria-label="Main">
        <ul class="mega-nav__items">
            <li v-for="item in navigationData" :key="item.id" :class="['mega-nav__item',{'mega-nav__item--has-dropdown': hasSubNav(item)}]" @mouseenter="updateActiveSubItem(getFirstSubNavItem(item), null)" @mouseleave="resetActiveSubItem">
                <template v-if="item.url === '#'">
                    <div 
                        class="mega-nav__link" 
                        role="button"
                        tabindex="0"
                    >
                        {{ item.name }}
                        <template v-if="hasSubNav(item)">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                <path d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z"/>
                            </svg>
                        </template>
                    </div>
                </template>
                <template v-else>
                    <a 
                        class="mega-nav__link"
                        :class="{ active: isActive(item.url) }"
                        :href="item.url"
                        :target="item.target"
                    >
                        {{ item.name }}
                        <template v-if="hasSubNav(item)">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                <path d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z"/>
                            </svg>
                        </template>
                    </a>
                </template>

                <template v-if="hasSubNav(item) && item.subNavigation.length > 1">
                    <div class="mega-nav__sub-nav-wrapper mega-nav__sub-nav-wrapper--mega">
                        <div class="container">
                            <div :class="['mega-nav__sub-nav', item.subNavigation.length > 1 ? 'mega-nav__sub-nav--columns' : '']" :style="[item.subNavigation.length > 1 ? '--mega-menu-columns:' + item.subNavigation.length : '']">
                                <ul v-for="(subNav, index) in item.subNavigation" :key="index" class="mega-nav__sub-items">
                                    <li v-if="subNav.title" class="mega-nav__sub-label">
                                        <template v-if="subNav.feature">
                                            <a :href="subNav.feature.url" :target="subNav.feature.target">
                                                {{ subNav.title }}
                                            </a>
                                        </template>
                                        <template v-else>
                                            {{ subNav.title }}
                                        </template>
                                    </li>
                                    <li v-if="subNav.image" class="mega-nav__sub-image">
                                        <a v-if="subNav.feature" :href="subNav.feature.url" :target="subNav.feature.target">
                                            <img :width="subNav.image.width" :height="subNav.image.height" :src="subNav.image.url" :alt="subNav.image.altText" class="image-aspect-ratio image-aspect-ratio--3-2">
                                        </a>
                                        <template v-else>
                                            <img :width="subNav.image.width" :height="subNav.image.height" :src="subNav.image.url" :alt="subNav.image.altText" class="image-aspect-ratio image-aspect-ratio--3-2">
                                        </template>
                                    </li>
                                    <li v-for="subItem of subNav.items" class="mega-nav__sub-item"  :key="subItem.id">
                                        <a :class="['mega-nav__sub-link',{'active': pageUrl && pageUrl == subItem.url}]" :href="subItem.url" :target="subItem.target">
                                            {{ subItem.name }}
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </template>
                <template v-else-if="hasSubNav(item) && item.subNavigation.length <= 1">
                    <div class="mega-nav__sub-nav-wrapper mega-nav__sub-nav-wrapper--dropdown">
                        <div class="mega-nav__sub-nav mega-nav__sub-nav--list">
                            <ul v-for="(subNav, index) in item.subNavigation" :key="index" class="mega-nav__sub-items">
                                <li v-if="subNav.title" class="mega-nav__sub-label">
                                    <a v-if="subNav.feature" :href="subNav.feature.url" :target="subNav.feature.target" @mouseenter="updateActiveSubItem(subNav.feature, subNav.image)">
                                        {{ subNav.title }}
                                    </a>
                                    <template v-else>
                                        {{ subNav.title }}
                                    </template>
                                </li>
                                <li v-for="subItem of subNav.items" class="mega-nav__sub-item" :key="subItem.id">
                                    <a class="mega-nav__sub-link" :href="subItem.url" :target="subItem.target" @mouseenter="updateActiveSubItem(subItem, null)">
                                        {{ subItem.name }}
                                    </a>
                                </li>
                                <li v-if="subNav.feature" class="mega-nav__sub-item mega-nav__sub-item--feature">
                                    <a class="mega-nav__sub-link" :href="subNav.feature.url" :target="subNav.feature.target" @mouseenter="updateActiveSubItem(subNav.feature, subNav.image)">
                                        <span>{{ subNav.feature.name }}</span>
                                        <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 -960 960 960" width="20">
                                            <path d="M262.771-210.001 460.719-480 262.771-749.999h61.742L522.461-480 324.513-210.001h-61.742Zm241.692 0L702.411-480 504.463-749.999h61.742L764.153-480 566.205-210.001h-61.742Z"/>
                                        </svg>
                                    </a>
                                </li>
                            </ul>
                            <ul class="mega-nav__sub-items" v-if="activeSubItem && activeSubItem.image">
                                <li class="mega-nav__sub-image">
                                    <a :href="activeSubItem.url" :target="activeSubItem.target" :title="activeSubItem.name">
                                        <img :width="activeSubItem.image.width" :src="activeSubItem.image.url" :alt="activeSubItem.image.altText" class="image-aspect-ratio image-aspect-ratio--3-2">
                                    </a>
                                    <a class="mega-nav__sub-link" :href="activeSubItem.url" :target="activeSubItem.target">
                                        <p class="text-small">{{ activeSubItem.name }}</p>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </template>
            </li>
        </ul>
    </div>
</template>

<script>

export default {
    name: 'MegaNavigation',
    data() {
        return {
            subNavigation: [],
            activeSubItem: null,
            currentPath: window.location.pathname
        };
    },
    props: {
        navigationData: {
            type: Array,
            required: true,
            default: () => []
        }
    },
    computed: {
        pageUrl() {
            return window.location.pathname;
        }
    },
    methods: {
        isActive(url) {
            const linkPath = new URL(url, window.location.origin).pathname;
            return linkPath === this.currentPath;
        },
        getFirstSubNavItem(item) {
            if (item.subNavigation && item.subNavigation.length > 0) {
                const subNav = item.subNavigation[0];

                if (subNav && subNav.items.length > 0) {
                    return subNav.items[0];
                }
            }
            return null;
        },
        updateActiveSubItem(subItem, subImage) {
            if (subImage !== null) {
                subItem.image = subImage;
            }
            this.activeSubItem = { ...subItem };
        },
        resetActiveSubItem() {
            this.activeSubItem = null;
        },
        hasSubNav(item) {
            return item.subNavigation && item.subNavigation.length > 0;
        }
    }
}
</script>